import { useMemo, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

type Param = {
  name: string;
  type: string;
  required?: boolean;
  description?: string;
  validation?: string;
};

type ResponseDef = {
  status: number;
  description?: string;
  bodyExample?: unknown;
};

type ErrorDef = {
  status: number;
  message: string;
  when?: string;
};

type Endpoint = {
  id: string;
  title: string;
  path: string;
  method: string;
  auth: string;
  description: string;
  headers: Param[];
  query?: Param[];
  body?: Param[];
  responses: ResponseDef[];
  errors: ErrorDef[];
  refs?: string[];
  curl: string;
};

function ensureCurlAuth(curl: string, anonKey: string): string {
  if (!anonKey) return curl;
  if (curl.includes("Authorization: Bearer")) return curl;
  return `${curl}\n-H \"Authorization: Bearer ${anonKey}\"`;
}

function Section({ e, anonKey }: { e: Endpoint; anonKey: string }) {
  return (
    <section id={e.id} className="mb-8 border rounded-md p-4 bg-white shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{e.title}</h2>
        <span className={cn("px-3 py-1 text-xs rounded font-bold", e.method === "GET" ? "bg-blue-100 text-blue-800" : e.method === "POST" ? "bg-green-100 text-green-800" : e.method === "PATCH" ? "bg-yellow-100 text-yellow-800" : e.method === "DELETE" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800")}>{e.method}</span>
      </div>
      <div className="mt-2 text-sm">
        <div className="font-mono text-sm bg-gray-50 border rounded px-3 py-2 inline-block">{e.path}</div>
        <div className="mt-2"><span className="font-semibold">Autenticação:</span> <span className="font-medium">{e.auth}</span></div>
        <div className="mt-2 text-gray-700">{e.description}</div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold">Headers</h3>
          <ul className="mt-2 space-y-1">
            {e.headers.map((h) => (
              <li key={h.name} className="text-sm">
                <span className="font-mono">{h.name}</span>
                {" "}
                <span className="text-gray-500">({h.type}{h.required ? ", obrigatório" : ""})</span>
                {h.description ? <span className="ml-1">— {h.description}</span> : null}
              </li>
            ))}
          </ul>
        </div>
        <div>
          {e.query && e.query.length > 0 ? (
            <>
              <h3 className="text-sm font-semibold">Query Strings</h3>
              <ul className="mt-2 space-y-1">
                {e.query.map((q) => (
                  <li key={q.name} className="text-sm">
                    <span className="font-mono">{q.name}</span>
                    {" "}
                    <span className="text-gray-500">({q.type}{q.required ? ", obrigatório" : ""})</span>
                    {q.description ? <span className="ml-1">— {q.description}</span> : null}
                    {q.validation ? <span className="ml-1 text-gray-600">[{q.validation}]</span> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {e.body && e.body.length > 0 ? (
            <>
              <h3 className="text-sm font-semibold mt-4">Body</h3>
              <ul className="mt-2 space-y-1">
                {e.body.map((b) => (
                  <li key={b.name} className="text-sm">
                    <span className="font-mono">{b.name}</span>
                    {" "}
                    <span className="text-gray-500">({b.type}{b.required ? ", obrigatório" : ""})</span>
                    {b.description ? <span className="ml-1">— {b.description}</span> : null}
                    {b.validation ? <span className="ml-1 text-gray-600">[{b.validation}]</span> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        <h3 className="text-sm font-semibold">Respostas</h3>
        <ul className="mt-2 space-y-1">
          {e.responses.map((r, idx) => (
            <li key={idx} className="text-sm">
              <span className="font-mono">{r.status}</span>
              {r.description ? <span className="ml-1">— {r.description}</span> : null}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Exemplo cURL</h3>
          <button
            onClick={() => navigator.clipboard.writeText(e.curl)}
            className="px-2 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 font-semibold"
            data-cy={`copy-curl-${e.id}`}
          >
            Copiar
          </button>
        </div>
        <pre className="mt-2 p-3 bg-gray-50 border rounded text-xs font-mono whitespace-pre-wrap break-words overflow-x-hidden"><code>{ensureCurlAuth(e.curl, anonKey)}</code></pre>
      </div>

      <div className="mt-4">
        <h3 className="text-sm font-semibold">Erros Previstos</h3>
        <ul className="mt-2 space-y-1">
          {e.errors.map((er, idx) => (
            <li key={idx} className="text-sm">
              <span className="font-mono">{er.status}</span>
              <span className="ml-1">— {er.message}</span>
              {er.when ? <span className="ml-1 text-gray-600">({er.when})</span> : null}
            </li>
          ))}
        </ul>
      </div>

      {e.refs && e.refs.length > 0 ? (
        <div className="mt-4">
          <h3 className="text-sm font-semibold">Referências de Código</h3>
          <ul className="mt-2 space-y-1">
            {e.refs.map((ref) => (
              <li key={ref} className="text-xs font-mono text-gray-600">{ref}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default function ApiReference() {
  const [anonKey, setAnonKey] = useState<string>("");
  const [apiKeys, setApiKeys] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");

  useEffect(() => {
    const k = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || "";
    setAnonKey(k);
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      const tenantId = (window as any)?.CAIXA_PRO_TENANT_ID || null;
      if (!tenantId && !user?.id) return;
      const { data, error } = await supabase
        .from("api_keys" as any)
        .select("key_full, enabled")
        .order("created_at", { ascending: false }) as any;
      if (!error && Array.isArray(data)) {
        setApiKeys(data.filter((r: any) => r.enabled && r.key_full).map((r: any) => r.key_full));
      }
    });
  }, []);

  const endpoints: Endpoint[] = useMemo(() => {
    const BASE = (import.meta as any).env?.VITE_SUPABASE_FUNCTIONS_URL || "https://<seu-projeto>.functions.supabase.co";
    const APIKEY = "<API_KEY>";
    const JWT = "<SUPABASE_JWT>";
    return [
    {
      id: "bank-accounts-get",
      title: "Bank Accounts — GET",
      path: "/functions/v1/bank_accounts[/:id]",
      method: "GET",
      auth: "API Key",
      description: "Lista contas bancárias do tenant com paginação; quando informado um id retorna somente a conta correspondente.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "limit", type: "number", description: "Tamanho da página (padrão 50)" },
        { name: "offset", type: "number", description: "Deslocamento (padrão 0)" },
        { name: "select", type: "string", description: "Colunas a retornar, ex.: id,nome" },
      ],
      responses: [
        { status: 200, description: "{ data: BankAccount[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
        { status: 405, message: "Método não suportado" },
        { status: 400, message: "Erro de consulta ou validação de escopo" },
      ],
      refs: [
        "supabase/functions/bank_accounts/index.ts:40-49",
        "supabase/functions/_shared/auth/api-keys.ts:18-23",
      ],
      curl: `curl -X GET "${BASE}/bank_accounts?limit=50" -H "apikey: ${APIKEY}"`
    },
    {
      id: "entities-search-post",
      title: "Semantic Entities Search — POST",
      path: "/functions/v1/ai-qdrant-search-entities",
      method: "POST",
      auth: "API Key ou JWT Supabase",
      description: "Busca semântica de entidades (produtos, clientes, fornecedores, etc.) do tenant no índice vetorial, retornando os itens mais semelhantes ao texto consultado.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string", description: "JWT de usuário (Authorization: Bearer) ou apikey" },
      ],
      body: [
        { name: "tenant_id", type: "string", required: true, description: "Tenant alvo" },
        { name: "query", type: "string", required: true, description: "Texto da busca semântica" },
        { name: "types", type: "string[]", description: "Filtra por tipos: products, clients, suppliers, expense_categories, bank_accounts, profiles, transactions" },
        { name: "limit", type: "number", description: "Top‑K máximo (padrão 10)" },
        { name: "threshold", type: "number", description: "Score mínimo (0–1) para retornar" },
      ],
      responses: [
        { status: 200, description: "{ ok, items: [{ entity_type, entity_id, title, score, attributes, payload }], request_id }" },
      ],
      errors: [
        { status: 400, message: "Parâmetros ausentes/invalidos" },
        { status: 502, message: "Falha de embedding" },
        { status: 502, message: "Falha ao consultar Qdrant" },
      ],
      refs: ["supabase/functions/ai-qdrant-entities-batch/index.ts:1-106", "supabase/functions/_shared/qdrant/service.ts:1-75"],
      curl: `curl -X POST \"${BASE}/ai-qdrant-search-entities\" \\n+-H \"Content-Type: application/json\" \\n+-H \"apikey: ${APIKEY}\" \\n+-d '{"tenant_id":"<tenant>","query":"café expresso","types":["products"],"limit":10,"threshold":0.35}'`
    },
    {
      id: "kb-search-post",
      title: "Semantic KB Texts Search — POST",
      path: "/functions/v1/ai-qdrant-search-texts",
      method: "POST",
      auth: "API Key ou JWT Supabase",
      description: "Busca semântica em textos previamente indexados das bases de conhecimento do tenant, retornando trechos relevantes para a consulta.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string" },
      ],
      body: [
        { name: "tenant_id", type: "string", required: true },
        { name: "kb_id", type: "string", description: "Opcional; restringe a uma base específica" },
        { name: "query", type: "string", required: true },
        { name: "limit", type: "number", description: "Top‑K máximo (padrão 10)" },
        { name: "threshold", type: "number", description: "Score mínimo (0–1)" },
      ],
      responses: [
        { status: 200, description: "{ ok, items: [{ text_id, kb_id, title, content_snippet, score, payload }], request_id }" },
      ],
      errors: [
        { status: 400, message: "Parâmetros ausentes/invalidos" },
        { status: 502, message: "Falha de embedding" },
        { status: 502, message: "Falha ao consultar Qdrant" },
      ],
      refs: ["supabase/functions/ai-qdrant-index/index.ts:1-70"],
      curl: `curl -X POST \"${BASE}/ai-qdrant-search-texts\" \\n+-H \"Content-Type: application/json\" \\n+-H \"apikey: ${APIKEY}\" \\n+-d '{"tenant_id":"<tenant>","query":"como configurar caixa","limit":10}'`
    },
    {
      id: "entities-upsert-post",
      title: "Entities Upsert — POST",
      path: "/functions/v1/ai-qdrant-entities-upsert",
      method: "POST",
      auth: "API Key ou JWT Supabase",
      description: "Gera embedding e insere/atualiza a representação vetorial de uma entidade do tenant no Qdrant para buscas semânticas.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string" },
      ],
      body: [
        { name: "tenant_id", type: "string", required: true },
        { name: "entity_type", type: "string", required: true, description: "products|clients|suppliers|expense_categories|bank_accounts|profiles|transactions" },
        { name: "entity_id", type: "string", required: true, description: "UUID" },
        { name: "title", type: "string" },
        { name: "content", type: "string", required: true, description: "Texto sumarizado para embedding" },
        { name: "source_table", type: "string" },
        { name: "attributes", type: "object" },
        { name: "tags", type: "string[]" },
      ],
      responses: [
        { status: 200, description: "{ ok: true }" },
      ],
      errors: [
        { status: 400, message: "EMBEDDING_EMPTY" },
        { status: 502, message: "QDRANT_UPSERT_FAILED" },
      ],
      refs: ["supabase/functions/ai-qdrant-entities-upsert/index.ts:1-61"],
      curl: `curl -X POST \"${BASE}/ai-qdrant-entities-upsert\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"tenant_id":"<tenant>","entity_type":"products","entity_id":"<uuid>","title":"Café Expresso","content":"Café expresso 250g torrado"}'`
    },
    {
      id: "entities-remove-post",
      title: "Entities Remove — POST",
      path: "/functions/v1/ai-qdrant-entities-remove",
      method: "POST",
      auth: "API Key ou JWT Supabase",
      description: "Remove uma entidade previamente indexada do Qdrant, deixando de aparecer em buscas semânticas.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string" },
      ],
      body: [
        { name: "tenant_id", type: "string", required: true },
        { name: "entity_type", type: "string", required: true },
        { name: "entity_id", type: "string", required: true },
      ],
      responses: [
        { status: 200, description: "{ ok: true }" },
      ],
      errors: [
        { status: 502, message: "QDRANT_DELETE_FAILED" },
      ],
      refs: ["supabase/functions/ai-qdrant-entities-remove/index.ts:1-36"],
      curl: `curl -X POST \"${BASE}/ai-qdrant-entities-remove\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"tenant_id":"<tenant>","entity_type":"products","entity_id":"<uuid>"}'`
    },
    {
      id: "entities-batch-post",
      title: "Entities Batch — POST",
      path: "/functions/v1/ai-qdrant-entities-batch",
      method: "POST",
      auth: "API Key ou JWT Supabase",
      description: "Indexa em lote entidades de um tipo específico do tenant, com relatório de sucesso, falhas e itens ignorados.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string" },
      ],
      body: [
        { name: "tenant_id", type: "string", required: true },
        { name: "entity_type", type: "string", required: true },
      ],
      responses: [
        { status: 200, description: "{ ok, indexed, failed, skipped, errors_sample, error_counts, request_id }" },
      ],
      errors: [
        { status: 400, message: "TYPE_UNSUPPORTED" },
        { status: 500, message: "DB_FETCH_FAILED" },
        { status: 502, message: "EMBEDDING_FAILED|EMBEDDING_EMPTY|QDRANT_UPSERT_FAILED" },
      ],
      refs: ["supabase/functions/ai-qdrant-entities-batch/index.ts:1-106"],
      curl: `curl -X POST \"${BASE}/ai-qdrant-entities-batch\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"tenant_id":"<tenant>","entity_type":"products"}'`
    },
    {
      id: "kb-index-text-post",
      title: "KB Index Text — POST",
      path: "/functions/v1/ai-qdrant-index",
      method: "POST",
      auth: "API Key ou JWT Supabase",
      description: "Indexa um texto de uma base de conhecimento do tenant no Qdrant para habilitar buscas semânticas futuras.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string" },
      ],
      body: [
        { name: "tenant_id", type: "string", required: true },
        { name: "kb_id", type: "string", required: true },
        { name: "text_id", type: "string", required: true },
        { name: "title", type: "string" },
        { name: "content", type: "string", required: true },
        { name: "collection", type: "string", description: "Opcional; padrão configurado" },
      ],
      responses: [
        { status: 200, description: "{ ok: true }" },
      ],
      errors: [
        { status: 400, message: "QDRANT_URL não configurado" },
        { status: 502, message: "EMBEDDING_FAILED" },
        { status: 502, message: "QDRANT_COLLECTION_FAILED" },
        { status: 502, message: "QDRANT_UPSERT_FAILED" },
      ],
      refs: ["supabase/functions/ai-qdrant-index/index.ts:1-70"],
      curl: `curl -X POST \"${BASE}/ai-qdrant-index\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"tenant_id":"<tenant>","kb_id":"<kb>","text_id":"<text>","title":"Guia de caixa","content":"Texto a ser indexado"}'`
    },
    {
      id: "mcp-manifest-get",
      title: "MCP Manifest — GET",
      path: "/functions/v1/mcp-gateway/.well-known/mcp",
      method: "GET",
      auth: "Opcional (retorna catálogo conforme cabeçalhos)",
      description: "Expõe o manifesto MCP com metadata do servidor e catálogo de ferramentas disponíveis para chamadas programáticas.",
      headers: [
        { name: "Authorization|apikey", type: "string", description: "Opcional; filtra catálogo por permissões" },
      ],
      responses: [
        { status: 200, description: "{ version, server, tools }" },
      ],
      errors: [],
      refs: ["supabase/functions/mcp-gateway/index.ts:156-163"],
      curl: `curl -X GET \"${BASE}/mcp-gateway/.well-known/mcp\"`
    },
    {
      id: "mcp-tools-get",
      title: "MCP Tools — GET",
      path: "/functions/v1/mcp-gateway/tools",
      method: "GET",
      auth: "Opcional; Authorization (usuário) ou apikey (RBAC)",
      description: "Lista as ferramentas MCP disponíveis para o chamador, ajustando visibilidade conforme autorização ou escopos da API Key.",
      headers: [
        { name: "Authorization|apikey", type: "string", description: "Opcional; filtra tools conforme escopos" },
      ],
      query: [
        { name: "apikey", type: "string", description: "Opcional; alternativa quando o cliente só permite um header" },
      ],
      responses: [
        { status: 200, description: "{ ok: true, tools }" },
      ],
      errors: [],
      refs: ["supabase/functions/mcp-gateway/index.ts:164-185"],
      curl: `curl -X GET \"${BASE}/mcp-gateway/tools?apikey=${APIKEY}\"`
    },
    {
      id: "mcp-call-post",
      title: "MCP Call — POST",
      path: "/functions/v1/mcp-gateway/call",
      method: "POST",
      auth: "Authorization para tools 'user' | apikey para tools 'apikey'",
      description: "Executa uma tool MCP com argumentos fornecidos, retornando dados estruturados; aplica regras de autorização conforme o tipo da ferramenta.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string", required: true, description: "Obrigatório conforme a tool" },
      ],
      query: [
        { name: "apikey", type: "string", description: "Opcional; alternativa quando o cliente só permite um header" },
      ],
      body: [
        { name: "tool", type: "string", required: true, description: "Nome da tool (ex.: transactions_create)" },
        { name: "args", type: "object", required: true, description: "Argumentos conforme schema da tool" },
      ],
      responses: [
        { status: 200, description: "{ ok: true, tool, data }" },
      ],
      errors: [
        { status: 404, message: "tool não encontrada" },
        { status: 401, message: "não autorizado/apikey ausente", when: "quando cabeçalho obrigatório não enviado" },
        { status: 403, message: "permissão insuficiente", when: "apikey sem escopo necessário" },
        { status: 400, message: "erro de validação/execução" },
      ],
      refs: ["supabase/functions/mcp-gateway/index.ts:168-214", "supabase/functions/mcp-gateway/index.ts:215-284"],
      curl: `curl -X POST \"${BASE}/mcp-gateway/call?apikey=${APIKEY}\" \
 -H \"Content-Type: application/json\" \
 -d '{"tool":"transactions_create","args":{"amount":100}}'`
    },
    {
      id: "transactions-get",
      title: "Transactions — GET",
      path: "/functions/v1/transactions[/:id]",
      method: "GET",
      auth: "API Key (Authorization: Bearer prefixo.segredo ou apikey)",
      description: "Lista transações do tenant com paginação e filtro por status; quando informado um id retorna somente a transação correspondente.",
      headers: [
        { name: "Content-Type", type: "application/json" },
        { name: "Authorization|apikey", type: "string", required: true, description: "Enviar Bearer ou apikey com prefixo.segredo" },
      ],
      query: [
        { name: "limit", type: "number", description: "Tamanho da página (padrão 50)" },
        { name: "offset", type: "number", description: "Deslocamento (padrão 0)" },
        { name: "status", type: "string", description: "Filtra por status" },
        { name: "select", type: "string", description: "Colunas a retornar, ex.: id,status,amount" },
      ],
      responses: [
        { status: 200, description: "{ data: Transaction[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
        { status: 401, message: "Formato de apikey inválido" },
        { status: 401, message: "apikey não encontrada/desativada/expirada/inválida" },
        { status: 405, message: "Método não suportado" },
        { status: 400, message: "Erro de consulta ou validação de escopo" },
      ],
      refs: ["supabase/functions/transactions/index.ts:40-52", "supabase/functions/_shared/auth/api-keys.ts:18-23", "supabase/functions/_shared/auth/api-keys.ts:33-70"],
      curl: `curl -X GET \"${BASE}/transactions?limit=50&status=approved\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "transactions-post",
      title: "Transactions — POST",
      path: "/functions/v1/transactions",
      method: "POST",
      auth: "API Key",
      description: "Cria uma nova transação para o tenant, definindo tipo, método de pagamento e valor; vincula automaticamente o vendedor ao perfil da API Key quando aplicável.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "select", type: "string", description: "Colunas a retornar do registro criado" },
      ],
      body: [
        { name: "transaction_type", type: "string", description: "Tipo da transação" },
        { name: "payment_method", type: "string", description: "Método de pagamento" },
        { name: "amount", type: "number", description: "Valor" },
        { name: "vendedor_id", type: "string", description: "Sobrescrito pelo perfil quando keyType=user" },
      ],
      responses: [
        { status: 201, description: "{ data: Transaction }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
        { status: 400, message: "Falhas de inserção" },
      ],
      refs: ["supabase/functions/transactions/index.ts:53-61"],
      curl: `curl -X POST \"${BASE}/transactions\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"transaction_type":"payment","payment_method":"cash","amount":100.00}'`
    },
    {
      id: "transactions-patch",
      title: "Transactions — PATCH",
      path: "/functions/v1/transactions/:id",
      method: "PATCH",
      auth: "API Key",
      description: "Atualiza campos de uma transação existente do tenant, identificada por id na URL.",
      headers: [
        { name: "Content-Type", type: "application/json", required: true },
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "select", type: "string", description: "Colunas a retornar do registro atualizado" },
      ],
      body: [
        { name: "...", type: "any", description: "Campos para atualização" },
      ],
      responses: [
        { status: 200, description: "{ data: Transaction }" },
      ],
      errors: [
        { status: 400, message: "ID não informado na URL" },
        { status: 404, message: "Registro não encontrado" },
      ],
      refs: ["supabase/functions/transactions/index.ts:62-71"],
      curl: `curl -X PATCH \"${BASE}/transactions/<id>\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"status":"approved"}'`
    },
    {
      id: "transactions-delete",
      title: "Transactions — DELETE",
      path: "/functions/v1/transactions/:id",
      method: "DELETE",
      auth: "API Key",
      description: "Exclui uma transação do tenant pelo id informado na URL.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      responses: [
        { status: 204, description: "Sem corpo" },
      ],
      errors: [
        { status: 400, message: "ID não informado na URL" },
      ],
      refs: ["supabase/functions/transactions/index.ts:72-78"],
      curl: `curl -X DELETE \"${BASE}/transactions/<id>\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "transaction-items-get",
      title: "Transaction Items — GET",
      path: "/functions/v1/transaction_items[/:id]",
      method: "GET",
      auth: "API Key",
      description: "Lista itens de transação vinculados a transações do tenant; suporta paginação e busca por id.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "limit", type: "number" },
        { name: "offset", type: "number" },
        { name: "select", type: "string", description: "Colunas a retornar, ex.: id,quantity" },
      ],
      responses: [
        { status: 200, description: "{ data: TransactionItem[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
      ],
      refs: ["supabase/functions/transaction_items/index.ts:40-59"],
      curl: `curl -X GET \"${BASE}/transaction_items?limit=50\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "transaction-items-post",
      title: "Transaction Items — POST",
      path: "/functions/v1/transaction_items",
      method: "POST",
      auth: "API Key",
      description: "Adiciona um item a uma transação existente, validando que a transação pertence ao tenant da API Key.",
      headers: [
        { name: "Content-Type", type: "application/json" },
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "select", type: "string", description: "Colunas a retornar do item criado" },
      ],
      body: [
        { name: "transaction_id", type: "string", required: true, description: "Obrigatório e deve pertencer ao tenant" },
      ],
      responses: [
        { status: 201, description: "{ data: TransactionItem }" },
      ],
      errors: [
        { status: 400, message: "transaction_id é obrigatório" },
        { status: 404, message: "Transação não pertence ao tenant" },
      ],
      refs: ["supabase/functions/transaction_items/index.ts:60-71"],
      curl: `curl -X POST \"${BASE}/transaction_items\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"transaction_id":"<trx_id>","product_id":"<prod_id>","quantity":1}'`
    },
    {
      id: "transaction-payments-get",
      title: "Transaction Payments — GET",
      path: "/functions/v1/transaction_payments[/:id]",
      method: "GET",
      auth: "API Key",
      description: "Lista pagamentos associados a transações do tenant; suporta paginação e busca por id.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "limit", type: "number" },
        { name: "offset", type: "number" },
        { name: "select", type: "string", description: "Colunas a retornar, ex.: id,amount" },
      ],
      responses: [
        { status: 200, description: "{ data: TransactionPayment[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
      ],
      refs: ["supabase/functions/transaction_payments/index.ts:19-35"],
      curl: `curl -X GET \"${BASE}/transaction_payments?limit=50\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "clients-get",
      title: "Clients — GET",
      path: "/functions/v1/clients[/:id]",
      method: "GET",
      auth: "API Key",
      description: "Lista clientes do tenant com paginação, permite filtrar por nome e buscar por id.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "limit", type: "number" },
        { name: "offset", type: "number" },
        { name: "q", type: "string", description: "Pesquisa por nome (ilike)" },
        { name: "select", type: "string", description: "Colunas a retornar, ex.: id,nome" },
      ],
      responses: [
        { status: 200, description: "{ data: Client[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
      ],
      refs: ["supabase/functions/clients/index.ts:40-52"],
      curl: `curl -X GET \"${BASE}/clients?q=joao\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "products-get",
      title: "Products — GET",
      path: "/functions/v1/products[/:id]",
      method: "GET",
      auth: "API Key",
      description: "Lista produtos do tenant com paginação, permite filtrar por nome e buscar por id.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "limit", type: "number" },
        { name: "offset", type: "number" },
        { name: "q", type: "string", description: "Pesquisa por nome (ilike)" },
        { name: "select", type: "string", description: "Colunas a retornar, ex.: id,nome,preco" },
      ],
      responses: [
        { status: 200, description: "{ data: Product[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
      ],
      refs: ["supabase/functions/products/index.ts:46-68"],
      curl: `curl -X GET \"${BASE}/products?q=cafe\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "suppliers-get",
      title: "Suppliers — GET",
      path: "/functions/v1/suppliers[/:id]",
      method: "GET",
      auth: "API Key",
      description: "Lista fornecedores do tenant, suporta paginação, filtro por nome e busca por id.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "limit", type: "number" },
        { name: "offset", type: "number" },
        { name: "q", type: "string", description: "Pesquisa por nome (ilike)" },
        { name: "select", type: "string", description: "Colunas a retornar, ex.: id,nome" },
      ],
      responses: [
        { status: 200, description: "{ data: Supplier[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
      ],
      refs: ["supabase/functions/suppliers/index.ts:40-52"],
      curl: `curl -X GET \"${BASE}/suppliers?q=torrefacao\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "cash-sessions-get",
      title: "Cash Register Sessions — GET",
      path: "/functions/v1/cash_register_sessions[/:id]",
      method: "GET",
      auth: "API Key",
      description: "Lista sessões de caixa do tenant com ordenação por abertura; suporta paginação e id.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "limit", type: "number" },
        { name: "offset", type: "number" },
        { name: "select", type: "string", description: "Colunas a retornar, ex.: id,opened_at" },
      ],
      responses: [
        { status: 200, description: "{ data: Session[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
      ],
      refs: ["supabase/functions/cash_register_sessions/index.ts:40-50"],
      curl: `curl -X GET \"${BASE}/cash_register_sessions?limit=20\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "cash-breakdown-post",
      title: "Cash Register Breakdown — POST",
      path: "/functions/v1/cash_register_breakdown",
      method: "POST",
      auth: "API Key",
      description: "Registra detalhamentos de caixa para uma sessão específica que pertence ao tenant.",
      headers: [
        { name: "Content-Type", type: "application/json" },
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "select", type: "string", description: "Colunas a retornar do breakdown criado" },
      ],
      body: [
        { name: "session_id", type: "string", required: true, description: "Deve pertencer ao tenant" },
      ],
      responses: [
        { status: 201, description: "{ data: Breakdown }" },
      ],
      errors: [
        { status: 400, message: "session_id é obrigatório" },
        { status: 404, message: "Sessão não pertence ao tenant" },
      ],
      refs: ["supabase/functions/cash_register_breakdown/index.ts:60-75"],
      curl: `curl -X POST \"${BASE}/cash_register_breakdown\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"session_id":"<session>","type":"in","amount":50.00,"description":"Troco"}'`
    },
    {
      id: "tenants-post",
      title: "Tenants — POST (criação de empresa/usuário)",
      path: "/functions/v1/tenants",
      method: "POST",
      auth: "API Key",
      description: "Cria um usuário e provisiona o tenant inicial com dados da empresa; retorna identificadores para integração.",
      headers: [
        { name: "Content-Type", type: "application/json" },
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      body: [
        { name: "email", type: "string", required: true, validation: "email válido" },
        { name: "password", type: "string", description: "Obrigatório se generate_password=false" },
        { name: "generate_password", type: "boolean", description: "Se true, gera senha aleatória" },
        { name: "company_name", type: "string", required: true },
        { name: "company_slug", type: "string", description: "Opcional; gerado do nome se não enviado" },
        { name: "full_name", type: "string", description: "Nome completo do usuário" },
      ],
      responses: [
        { status: 201, description: "{ success: true, user_id, tenant_id, tenant }" },
      ],
      errors: [
        { status: 400, message: "Campos obrigatórios: email, password|generate_password, company_name" },
        { status: 400, message: "Erro ao criar usuário" },
        { status: 400, message: "Usuário não retornado" },
      ],
      refs: ["supabase/functions/tenants/index.ts:51-112"],
      curl: `curl -X POST \"${BASE}/tenants\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"email":"user@empresa.com","generate_password":true,"company_name":"Empresa XYZ","full_name":"Usuário Admin"}'`
    },
    {
      id: "subscription-coupons-get",
      title: "Subscription Coupons — GET",
      path: "/functions/v1/subscription_coupons[/:id]",
      method: "GET",
      auth: "API Key",
      description: "Lista cupons de assinatura disponíveis; suporta paginação e busca por id.",
      headers: [
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      query: [
        { name: "limit", type: "number" },
        { name: "offset", type: "number" },
      ],
      responses: [
        { status: 200, description: "{ data: Coupon[] }" },
      ],
      errors: [
        { status: 401, message: "Header apikey ausente" },
      ],
      refs: ["supabase/functions/subscription_coupons/index.ts:676-686"],
      curl: `curl -X GET \"${BASE}/subscription_coupons?limit=50\" -H \"apikey: ${APIKEY}\"`
    },
    {
      id: "subscription-coupons-validate",
      title: "Subscription Coupons — POST validate",
      path: "/functions/v1/subscription_coupons",
      method: "POST",
      auth: "API Key",
      description: "Valida um cupom para um usuário e plano, verificando janelas de validade, status, limites e planos permitidos.",
      headers: [
        { name: "Content-Type", type: "application/json" },
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      body: [
        { name: "action", type: "string", required: true, validation: "'validate'" },
        { name: "user_id", type: "string", required: true },
        { name: "plan_id", type: "string", required: true },
        { name: "code", type: "string", required: true, description: "Código do cupom" },
      ],
      responses: [
        { status: 200, description: "{ is_valid, normalized_code, must_pay, discounted_amount, duration, description, image_url }" },
      ],
      errors: [
        { status: 400, message: "Parâmetros inválidos" },
        { status: 200, message: "is_valid=false", when: "not_found/inactive/before_start/after_end/plan_not_allowed/limits" },
      ],
      refs: ["supabase/functions/subscription_coupons/index.ts:688-733"],
      curl: `curl -X POST \"${BASE}/subscription_coupons\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"action":"validate","user_id":"<user>","plan_id":"<plan>","code":"PROMO10"}'`
    },
    {
      id: "subscription-coupons-apply",
      title: "Subscription Coupons — POST apply",
      path: "/functions/v1/subscription_coupons",
      method: "POST",
      auth: "API Key",
      description: "Aplica um cupom à assinatura do tenant, podendo gerar transação de cobrança e atualizar o estado da assinatura e próxima fatura.",
      headers: [
        { name: "Content-Type", type: "application/json" },
        { name: "Authorization|apikey", type: "string", required: true },
      ],
      body: [
        { name: "action", type: "string", required: true, validation: "'apply'" },
        { name: "tenant_id", type: "string", required: true },
        { name: "user_id", type: "string", required: true },
        { name: "plan_id", type: "string", required: true },
        { name: "transaction_id", type: "string" },
        { name: "code", type: "string", required: true },
      ],
      responses: [
        { status: 200, description: "{ success: true, subscription_transaction_id }" },
      ],
      errors: [
        { status: 400, message: "Parâmetros inválidos" },
        { status: 400, message: "Cupom inválido/fora de validade/não aplicável/limites excedidos" },
      ],
      refs: ["supabase/functions/subscription_coupons/index.ts:734-799"],
      curl: `curl -X POST \"${BASE}/subscription_coupons\" \
-H \"Content-Type: application/json\" \
-H \"apikey: ${APIKEY}\" \
-d '{"action":"apply","tenant_id":"<tenant>","user_id":"<user>","plan_id":"<plan>","code":"PROMO10"}'`
    },
    {
      id: "ai-generate-text-post",
      title: "AI Generate Text — POST",
      path: "/functions/v1/ai-generate-text",
      method: "POST",
      auth: "JWT Supabase (Authorization: Bearer {jwt})",
      description: "Gera texto usando provedores de IA configurados para o tenant, respeitando limites de uso e registrando a requisição e ativos gerados.",
      headers: [
        { name: "Content-Type", type: "application/json" },
        { name: "Authorization", type: "string", required: true, description: "JWT de usuário do Supabase" },
      ],
      body: [
        { name: "prompt", type: "string", required: true },
        { name: "attachments", type: "Array<{type,mimeType,url}>" },
        { name: "provider_hint", type: "string" },
      ],
      responses: [
        { status: 200, description: "{ success, request_id, provider, model, text, usage, metadata }" },
      ],
      errors: [
        { status: 405, message: "Método não suportado" },
        { status: 400, message: "Não autenticado/tenant não encontrado/limite de uso/erros de geração" },
      ],
      refs: ["supabase/functions/ai-generate-text/index.ts:20-25", "supabase/functions/ai-generate-text/index.ts:27-59", "supabase/functions/ai-generate-text/index.ts:151-165"],
      curl: `curl -X POST \"${BASE}/ai-generate-text\" \
-H \"Content-Type: application/json\" \
-H \"Authorization: Bearer ${JWT}\" \
-d '{"prompt":"Escreva um texto promocional sobre café expresso"}'`
    },
    {
      id: "mercadopago-webhook-post",
      title: "Mercado Pago Webhook — POST",
      path: "/functions/v1/mercadopago-webhook",
      method: "POST",
      auth: "Sem autenticação (entrada do MP)",
      description: "Recebe notificações de eventos do Mercado Pago e aciona o processamento interno associado à cobrança e assinaturas.",
      headers: [
        { name: "Content-Type", type: "application/json" },
      ],
      responses: [
        { status: 200, description: "Processamento OK (varia conforme evento)" },
      ],
      errors: [
        { status: 400, message: "Erros de processamento" },
      ],
      refs: ["supabase/functions/mercadopago-webhook/index.ts:13-16", "supabase/functions/mercadopago-webhook/index.ts:39-58"],
      curl: `curl -X POST \"${BASE}/mercadopago-webhook\" \
-H \"Content-Type: application/json\" \
-d '{"type":"payment","data":{"id":"123"}}'`
    },
    {
      id: "chatwoot-webhook-post",
      title: "Chatwoot Webhook — POST",
      path: "/functions/v1/chatwoot-webhook",
      method: "POST",
      auth: "Assinatura opcional (x-chatwoot-signature)",
      description: "Recebe webhooks do Chatwoot para eventos de conversas e mensagens; quando configurado, valida a assinatura para garantir origem confiável.",
      headers: [
        { name: "Content-Type", type: "application/json" },
        { name: "x-chatwoot-signature", type: "string", description: "Se configurado CHATWOOT_WEBHOOK_SECRET" },
      ],
      responses: [
        { status: 200, description: "Processamento OK" },
      ],
      errors: [
        { status: 400, message: "Erros de processamento/assinatura inválida" },
      ],
      refs: ["supabase/functions/chatwoot-webhook/index.ts:130-151"],
      curl: `curl -X POST \"${BASE}/chatwoot-webhook\" \
-H \"Content-Type: application/json\" \
-H \"x-chatwoot-signature: <assinatura>\" \
-d '{"event":"message_created","data":{"content":"Olá"}}'`
    },
  ];
  }, []);

  useEffect(() => {
    if (!selectedId && endpoints.length > 0) {
      setSelectedId(endpoints[0].id);
    }
  }, [endpoints, selectedId]);

  return (
    <div className="flex gap-4">
      <nav className="w-64 hidden md:block">
        <div className="sticky top-4">
          <h3 className="text-sm font-semibold">Endpoints</h3>
          <div className="mt-2">
          <ul className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
            {endpoints.map((e) => (
              <li key={e.id}>
                <a href={`#${e.id}`} className="text-sm text-orange-800 hover:underline">
                  {e.title}
                </a>
              </li>
            ))}
          </ul>
          </div>
        </div>
      </nav>
      <div className="flex-1">
        <div className="sticky top-2 z-10 bg-transparent mb-4">
          <label className="block text-sm font-semibold mb-1">Selecione um endpoint</label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full md:w-1/2 border rounded px-2 py-2 bg-white"
          >
            {endpoints.map((e) => {
              const d = e.description || "";
              const short = d.length > 72 ? d.slice(0, 72) + "…" : d;
              return (
                <option key={e.id} value={e.id}>{`${e.title} — ${short}`}</option>
              );
            })}
          </select>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <div className="text-xs font-semibold mb-1">apikey principal</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={apiKeys.length ? apiKeys[0] : "<API_KEY>"}
                  className="flex-1 border rounded px-2 py-2 font-mono text-xs bg-white"
                />
                <button
                  onClick={() => navigator.clipboard.writeText(apiKeys.length ? apiKeys[0] : "<API_KEY>")}
                  className="px-3 py-2 text-xs rounded bg-gray-200 hover:bg-gray-300"
                >
                  Copiar
                </button>
              </div>
            </div>
            {apiKeys.slice(1).map((k, idx) => (
              <div key={k}>
                <div className="text-xs font-semibold mb-1">apikey {idx + 2}</div>
                <div className="flex gap-2">
                  <input readOnly value={k} className="flex-1 border rounded px-2 py-2 font-mono text-xs bg-white" />
                  <button onClick={() => navigator.clipboard.writeText(k)} className="px-3 py-2 text-xs rounded bg-gray-200 hover:bg-gray-300">Copiar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        {selectedId ? (
          <Section e={endpoints.find((x) => x.id === selectedId)!} anonKey={anonKey} />
        ) : null}
      </div>
    </div>
  );
}
